"use client";

import React, { useState } from "react";
import { useMarkupStore } from "../store";
import { ALLOWED_EMAIL_DOMAIN } from "../config/access";
import {
    describeAuthError,
    resendVerification,
    resetPassword,
    signIn,
    signUp,
} from "../services/auth";

interface AuthFormProps {
    onSuccess: () => void;
}

const inputClass =
    "w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-matter-ink/30 focus:border-transparent transition-all";

export const AuthForm = ({ onSuccess }: AuthFormProps) => {
    const authNotice = useMarkupStore((s) => s.authNotice);
    const setAuthNotice = useMarkupStore((s) => s.setAuthNotice);
    const [mode, setMode] = useState<"login" | "signup">("login");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [info, setInfo] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    // A real <form> submit: Enter works, and the browser's required/minLength
    // checks run. The old submit was a clickable <div>, which skipped both.
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setInfo(null);
        setAuthNotice(null);
        setLoading(true);

        try {
            if (mode === "signup") {
                await signUp(email, password);
                setInfo(
                    "Account created. We’ve emailed you a verification link — click it, then sign in.",
                );
                setMode("login");
            } else {
                await signIn(email, password);
                onSuccess();
            }
        } catch (err) {
            setError(describeAuthError(err));
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        setError(null);
        try {
            await resendVerification();
            setInfo("Verification email sent. Check your inbox (and spam).");
        } catch (err) {
            setError(describeAuthError(err));
        }
    };

    const handleForgot = async () => {
        setError(null);
        setInfo(null);
        if (!email) {
            setError("Enter your email address above first.");
            return;
        }
        try {
            await resetPassword(email);
            setInfo("If there’s an account for that email, a reset link is on its way.");
        } catch (err) {
            setError(describeAuthError(err));
        }
    };

    const needsVerification = !!authNotice?.includes("verify");

    return (
        <div className="flex flex-col items-center justify-start h-full p-5 pt-12">
            <div className="w-full max-w-md">
                <div className="bg-white rounded-2xl shadow-2xl p-8">
                    <h2 className="text-2xl! font-bold text-gray-900 mb-2 text-center">
                        {mode === "login" ? "Sign In" : "Create Account"}
                    </h2>
                    <p className="text-gray-600 text-sm! text-center mb-6">
                        {mode === "login"
                            ? "Sign in to submit and manage feedback"
                            : `Use your @${ALLOWED_EMAIL_DOMAIN} email — we’ll send you a link to verify it`}
                    </p>

                    {authNotice && (
                        <div role="alert" className="mb-4 bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg text-sm!">
                            {authNotice}
                            {needsVerification && (
                                <button
                                    type="button"
                                    onClick={handleResend}
                                    className="block mt-2 font-semibold underline"
                                >
                                    Resend verification email
                                </button>
                            )}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label
                                htmlFor="markup-auth-email"
                                className="block text-sm! font-medium text-gray-700 mb-2"
                            >
                                Email Address
                            </label>
                            <input
                                id="markup-auth-email"
                                type="email"
                                autoComplete="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                className={inputClass}
                                placeholder={`you@${ALLOWED_EMAIL_DOMAIN}`}
                            />
                        </div>

                        <div>
                            <label
                                htmlFor="markup-auth-password"
                                className="block text-sm! font-medium text-gray-700 mb-2"
                            >
                                Password
                            </label>
                            <input
                                id="markup-auth-password"
                                type="password"
                                autoComplete={mode === "login" ? "current-password" : "new-password"}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                minLength={6}
                                className={inputClass}
                                placeholder="Minimum 6 characters"
                            />
                        </div>

                        {error && (
                            <div role="alert" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm!">
                                {error}
                            </div>
                        )}
                        {info && (
                            <div role="status" className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm!">
                                {info}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-matter-ink text-matter-cream py-2.5 px-4 rounded-lg font-medium hover:bg-matter-ink/80 transition-all cursor-pointer text-center disabled:opacity-60"
                        >
                            {loading
                                ? "Please wait..."
                                : mode === "login"
                                  ? "Sign In"
                                  : "Create Account"}
                        </button>
                    </form>

                    <div className="mt-6 flex flex-col items-center gap-2 text-center">
                        {mode === "login" && (
                            <button
                                type="button"
                                onClick={handleForgot}
                                className="text-sm! text-gray-600 hover:underline"
                            >
                                Forgot your password?
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={() => {
                                setMode(mode === "login" ? "signup" : "login");
                                setError(null);
                                setInfo(null);
                            }}
                            className="text-black font-medium cursor-pointer hover:underline"
                        >
                            {mode === "login"
                                ? "New here? Create an account"
                                : "Already have an account? Sign in"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
